import { useCallback, useEffect, useRef, useState } from 'react';
import { AuthorizationError } from '@ametista-ai/api-schema/errors';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button, Center, Loader, Stack, Text } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import { InternalNavbar } from '@/components/Navbar';
import { FlagsProvider } from '@/flags/FlagsProvider';
import { useCurrentAgency } from '@/queries/agencies/useCurrentAgency';
import { meKeys } from '@/queries/me/keys';
import { fetchMe, useMe } from '@/queries/me/useMe';
import { Layout } from '@/ui';
import { config } from '../../../config';
import { CreatePostModal } from '../CreatePostModal';
import { Bootstrap } from '../PrivateRoute/Bootstrap';
import { SearchSpotlight } from '../Search/SearchSpotlight';

export const InternalPrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const queryClient = useQueryClient();
  const { data: me } = useMe({ enabled: false });
  const { data: agency } = useCurrentAgency();
  const navigate = useNavigate();
  // BrowserRouter's navigate identity changes on every pathname change — keep a ref
  // so fetchSession stays stable and /me is only fetched once on mount.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const [fetchError, setFetchError] = useState<Error | null>(null);
  const [isFetching, setIsFetching] = useState(true);

  const fetchSession = useCallback(async () => {
    setFetchError(null);
    setIsFetching(true);
    try {
      const data = await queryClient.fetchQuery({
        queryKey: meKeys.all,
        queryFn: () => fetchMe(queryClient),
      });
      if (!data?.user) {
        navigateRef.current('/auth/login');
        return;
      }
      if (!data?.agency) {
        navigateRef.current('/onboarding');
        return;
      }

      if (data.agency!.status === 'pending') {
        navigateRef.current(`/onboarding/agency/${data.agency!.id}`);
        return;
      }

      if (data.agency.role !== 'admin') {
        navigateRef.current('/home');
        return;
      }
    } catch (error) {
      console.error(error);
      if (error instanceof AuthorizationError) {
        navigateRef.current('/auth/login');
      } else {
        Sentry.captureException(error);
        setFetchError(error instanceof Error ? error : new Error(String(error)));
      }
    } finally {
      setIsFetching(false);
    }
  }, [queryClient]);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  if (fetchError) {
    return (
      <Center h="100vh">
        <Stack align="center">
          <Text c="dimmed">Something went wrong. Please try again.</Text>
          <Button onClick={fetchSession}>Retry</Button>
        </Stack>
      </Center>
    );
  }

  if (isFetching) {
    return (
      <Center>
        <Loader />
      </Center>
    );
  }

  if (!agency || agency.status === 'pending') {
    return null;
  }

  if (agency?.role !== 'admin') {
    return null;
  }

  return (
    <FlagsProvider agency={agency} ldKey={config.launchDarkly.clientId}>
      <DatesProvider settings={{ locale: me?.user?.locale ?? 'en' }}>
        <Bootstrap>
          <Layout leftSidebar={<InternalNavbar />}>{children}</Layout>
          <SearchSpotlight />
          <CreatePostModal />
        </Bootstrap>
      </DatesProvider>
    </FlagsProvider>
  );
};
